import React, { useState, useMemo } from 'react'
import { getAllIcons, renderIcon } from 'app/lib/iconUtils'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'

interface IconSelectorProps {
    value?: string
    onChange: (iconName: string) => void
    label?: string
}

const IconSelector = ({ value, onChange, label = 'Select Icon' }: IconSelectorProps) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [isOpen, setIsOpen] = useState(false)

    // Get all available icons
    const allIcons = useMemo(() => getAllIcons(), [])

    // Filter icons based on search term
    const filteredIcons = useMemo(() => {
        if (!searchTerm.trim()) {
            return allIcons.slice(0, 100) // Show first 100 icons by default
        }

        const filtered = allIcons.filter(icon => icon.name.toLowerCase().includes(searchTerm.toLowerCase()))

        return filtered.slice(0, 100) // Limit to 100 results
    }, [searchTerm, allIcons])

    const handleIconClick = (iconName: string) => {
        onChange(iconName)
        setIsOpen(false)
        setSearchTerm('') // Reset search when closing
    }

    const clearIcon = () => {
        onChange('')
        setIsOpen(false)
    }

    return (
        <div className='space-y-2'>
            <Label>{label}</Label>

            {/* Current Icon Display */}
            <div className='flex flex-col items-center gap-3 p-3 border rounded-lg bg-background h-28'>
                <div className='flex items-center gap-2 flex-1'>
                    {value ? (
                        <>
                            <div className='w-6 h-6 flex items-center justify-center'>
                                {renderIcon(value, 'size-5')}
                            </div>
                            <span className='text-sm font-mono truncate'>{value}</span>
                        </>
                    ) : (
                        <span className='text-sm text-muted-foreground'>No icon selected</span>
                    )}
                </div>

                <div className='flex gap-2'>
                    {value && (
                        <Button type='button' variant='outline' size='sm' onClick={clearIcon}>
                            Clear
                        </Button>
                    )}

                    <Dialog open={isOpen} onOpenChange={setIsOpen}>
                        <DialogTrigger asChild>
                            <Button type='button' variant='outline' size='sm'>
                                {value ? 'Change' : 'Select'} Icon
                            </Button>
                        </DialogTrigger>
                        <DialogContent className='max-w-4xl max-h-[80vh] flex flex-col'>
                            <DialogHeader>
                                <DialogTitle>Select an Icon</DialogTitle>
                            </DialogHeader>

                            <div className='flex flex-col flex-1 min-h-0'>
                                {/* Search Input */}
                                <div className='mb-4'>
                                    <Input
                                        placeholder='Search icons... (e.g., home, user, arrow)'
                                        value={searchTerm}
                                        onChange={e => setSearchTerm(e.target.value)}
                                        className='w-full'
                                    />
                                </div>

                                {/* Results Count */}
                                <p className='text-sm text-muted-foreground mb-4'>
                                    Showing {filteredIcons.length} of {allIcons.length} icons
                                    {searchTerm && ` matching "${searchTerm}"`}
                                </p>

                                {/* Scrollable Icon Grid */}
                                <div className='flex-1 overflow-y-auto'>
                                    <div className='grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2 pb-4'>
                                        {filteredIcons.map(({ name, set }) => (
                                            <div
                                                key={name}
                                                onClick={() => handleIconClick(name)}
                                                className={`
                                                    flex flex-col items-center justify-center p-2 rounded-lg border cursor-pointer
                                                    hover:bg-secondary transition-colors
                                                    ${value === name ? 'bg-primary/10 border-primary' : 'border-border hover:border-primary/50'}
                                                `}
                                                title={`${name} (${set})`}
                                            >
                                                <div className='w-6 h-6 mb-1 flex items-center justify-center'>
                                                    {renderIcon(name, 'w-5 h-5')}
                                                </div>
                                                <span className='text-xs text-center truncate w-full'>
                                                    {name.replace(/^(Fa|Md|Ai|Bi|Bs|Fi|Hi|Hi2|Io|Io5|Ri|Tb|Lu)/, '')}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* No Results */}
                                    {filteredIcons.length === 0 && searchTerm && (
                                        <div className='text-center py-12'>
                                            <p className='font-medium text-muted-foreground'>
                                                No icons found matching "{searchTerm}"
                                            </p>
                                            <p className='text-sm mt-2 text-muted-foreground'>
                                                Try a different search term
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        </div>
    )
}

export default IconSelector
